<template>
  <div
    class="fts-run-as"
    :class="{'run-as--broadcast': broadcastMode}"
    v-if="(broadcastMode && runAs) || !broadcastMode"
  >
    <div class="run-as-container">
      <fts-tippy
        content="Run As"
        append-trigger-class="run-as-toggle"
        @trigger:click="open"
        v-if="!broadcastMode && !runAs"
      >
        <font-awesome-icon :icon="['far', 'user']" fixed-width />
        <div class="run-as-label">Run as</div>
      </fts-tippy>

      <template v-else>
        <font-awesome-icon
          :icon="['fas', 'times']"
          fixed-width
          class="run-as-close-action" 
          @click="close" 
        />
        
        <div class="run-as-label">Run as</div>

        <div class="run-as-content">
          <fts-dropdown-select
            append-to-body
            overlay
            :options="filteredOptions"
            v-model="mutableSelectedOption"
            content-class="dropdown-menu dropdown-menu-w-md dropdown-menu--sticky"
            :disabled="loading"
            class="run-as-dropdown"
            @hide="filterKeyword = null"
            v-if="isReady"
          >
            <template #toggle>
              <template v-if="selectedOption">
                <!-- member -->
                <fts-identity-display
                  :users="users"
                  identity-type="user"
                  :identity-name="selectedOption.name"
                  :identity-username="selectedOption.entityName"
                  :profile-photo-size="16"
                  v-if="selectedOption.type === 0"
                />
                <!-- user profile -->
                <fts-identity-display
                  :modules="workspaceModules"
                  :users="users"
                  identity-type="profile"
                  :identity-id="selectedOption.entryId"
                  :identity-name="selectedOption.name"
                  :emoji-size="16"
                  v-else-if="selectedOption.type === 5"
                />
                <!-- listing -->
                <fts-identity-display
                  :modules="workspaceModules"
                  :users="users"
                  identity-type="listing"
                  :identity-id="selectedOption.entryId"
                  :identity-emoji="selectedOption.emoji"
                  :identity-name="selectedOption.name"
                  :emoji-size="16"
                  class="auth-entity__identity"
                  v-else-if="selectedOption.type === 6"
                />
                <!-- system -->
                <fts-identity-display
                  :users="users"
                  identity-type="workspace"
                  :identity-name="selectedOption.name"
                  v-else
                />
              </template>

              <div v-else>
                (undefined)
              </div>
            </template>

            <div slot="menu-header" class="dropdown-filter">
              <div class="fts-form-control-filter">
                <input
                  class="fts-form-control"
                  v-model.trim="filterKeyword"
                  placeholder="Seach something for..."
                />
                <div class="search-icon">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </div>
              </div>
            </div>

            <div
              slot="option"
              slot-scope="{ option, select }"
              class="dropdown-option"
              @click="select(option)"
            >
              <!-- member -->
              <fts-identity-display
                :users="users"
                identity-type="user"
                :identity-name="option.name"
                :identity-username="option.entityName"
                :profile-photo-size="16"
                v-if="option.type === 0"
              />
              <!-- user profile -->
              <fts-identity-display
                :modules="workspaceModules"
                :users="users"
                identity-type="profile"
                :identity-id="option.entryId"
                :identity-name="option.name"
                :emoji-size="16"
                v-else-if="option.type === 5"
              />
              <!-- listing -->
              <fts-identity-display
                :modules="workspaceModules"
                :users="users"
                identity-type="listing"
                :identity-id="option.entryId"
                :identity-emoji="option.emoji"
                :identity-name="option.name"
                :emoji-size="16"
                class="auth-entity__identity"
                v-else-if="option.type === 6"
              />
              <!-- system -->
              <fts-identity-display
                :users="users"
                identity-type="workspace"
                :identity-name="option.name"
                v-else
              />
            </div>
          </fts-dropdown-select>

          <fts-spinner :size="14" class="run-state" v-if="!isReady || loading" />

          <fts-tippy
            :content="errorMessage"
            class="run-state"
            @trigger:click="retry"
            v-else-if="!!errorMessage"
          >
            <div style="display: flex;color: var(--fts-danger);cursor: pointer;">
              <i class="fts-icon-exclamation-triangle"></i>
            </div>
          </fts-tippy>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';

import FTSBtn from './FTSBtn';
import FTSDropdownSelect from './FTSDropdownSelect';
import FTSIdentityDisplay from './FTSIdentityDisplay';
import FTSReleaseBadge from './FTSReleaseBadge';
import FTSSpinner from './FTSSpinner';
import FTSTippy from './FTSTippy';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-identity-display": FTSIdentityDisplay,
    "fts-release-badge": FTSReleaseBadge,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy
  },
  props: {
    workspaceId: [String, Number],
    workspaceModules: Array,
    users: Array,
    runAs: Boolean,
    broadcastMode: Boolean
  },
  data: () => ({
    isReady: false,
    loading: false,
    readyError: null,
    error: null,
    options: [],
    selectedOption: null,
    filterKeyword: null
  }),
  methods: {
    open() {
      this.$emit("update:runAs", true);
    },
    close() {
      this.mutableSelectedOption = null;
      this.$emit("update:runAs", false)
    },
    async loadOptions() {
      try {
        this.isReady = false;
        this.readyError = null;
        const res = await canvasDesignStore.dispatch("retrieveAuthorizationEntities", {
          runAs: true
        });
        this.mutableSelectedOption = res.data.find(x => x.type == 0 && x.entityName == authorizationStore.getters.userName);
        this.options = _.chain(res.data)
          .orderBy(["type"], ["desc"])
          .map(x => {
            if ((x.name === "Everyone" || x.name === "Guest") && !x.id)
              return Object.assign(x, { id: x.name.toLowerCase() });

            return x;
          })
          .value();
      } catch(err) {
        console.error(err);
        this.readyError = "Failed to retrieve authorization entities.";
      } finally {
        this.isReady = true;
      }
    },
    select() {
      const { entityName } = {...this.selectedOption};
      this.error = null;
      this.$emit("selected", {
        target: !!entityName ? entityName : null,
        loading: (val) => this.loading = val,
        error: (val) => this.error = val
      });
    },
    retry() {
      if (this.readyError)
        this.loadOptions();
      else
        this.select();
    }
  },
  computed: {
    mutableSelectedOption: {
      get() {
        return this.selectedOption;
      },
      set(val) {
        this.selectedOption = val;
        this.select();
      }
    },
    filteredOptions() {
      const keyword = this.filterKeyword ? this.filterKeyword.toLowerCase() : null;

      return keyword ? this.options.filter(option =>
        ["name", "entityName"].some(key =>
          option[key] && option[key].toLowerCase().includes(keyword)
        )
      ) : this.options;
    },
    errorMessage() {
      if (!!this.readyError)
        return this.readyError;

      if (!!this.error)
        return this.error;
    }
  },
  watch: {
    runAs: {
      immediate: true,
      handler(val, oldVal) {
        if (!!val && val !== oldVal) {
          this.loadOptions();
        }
      }
    }
  }
}
</script>

<style lang="scss">
.fts-run-as {
  display: flex;
  align-items: center;
  padding: 0.375rem 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  .run-as-container {
    min-width: 0;
    display: flex;
    align-items: center;
  }
  .run-as-toggle {
    display: flex;
    align-items: center;
    color: var(--fts-secondary);
    > svg {
      margin-right: 0.5rem;
    }
    cursor: pointer;
  }
  .run-as-label {
    white-space: nowrap;
    color: var(--fts-secondary);
  }
  .run-as-content {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .run-as-dropdown {
    min-width: 0;
    margin-left: 0.375rem;
  }
  .run-state {
    margin-left: 0.75rem;
  }
  .run-as-close-action {
    margin-right: 0.5rem;
    font-size: 0.875rem;
    color: var(--fts-default);
    cursor: pointer;
  }
  &.run-as--broadcast {
    border-bottom: 1px solid var(--fts-border);
    .run-as-content {
      flex: 1;
    }
  }
}
</style>